﻿import { cardAnatomy, inputAnatomy, breadcrumbAnatomy, modalAnatomy } from "@chakra-ui/anatomy";
import { createMultiStyleConfigHelpers, defineStyle, defineStyleConfig, extendTheme } from "@chakra-ui/react";

const { definePartsStyle: defineCardPartsStyle, defineMultiStyleConfig: defineCardMultiStyleConfig } = createMultiStyleConfigHelpers(cardAnatomy.keys);
const { definePartsStyle: defineInputPartsStyle, defineMultiStyleConfig: defineInputMultiStyleConfig } = createMultiStyleConfigHelpers(inputAnatomy.keys);
const { definePartsStyle: defineBrdcmbPartsStyle, defineMultiStyleConfig: defineBrdcmbMultiStyleConf } = createMultiStyleConfigHelpers(breadcrumbAnatomy.keys);
const { definePartsStyle: defineModalPartsStyle, defineMultiStyleConfig: defineModalMultiStyleConfig } = createMultiStyleConfigHelpers(modalAnatomy.keys);

export const ApplicationLayoutBoxShadow = "2px 2px 7px rgba(0,0,0,.1)";

export const customTheme = {
    initialColorMode: "light",
    useSystemColorMode: false,
    borders: {},
    breakpoints: {},
    fontSizes: {},
    fontWeights: {},
    shadows: {},
    sizes: {},
    space: {},
    textStyles: {},
    letterSpacings: {},
    zIndices: {},
    semanticTokens: {},
    config: {},
    blur: {},
    layerStyles: {},
    transition: {},
    fonts: {
        heading: `'Helvetica Neue', Helvetica, Arial, sans-serif`,
        body: `'Helvetica Neue', Helvetica, Arial, sans-serif`,
    },
    styles: {
        global: {
            ":root": {
                boxSizing: "border-box",
                fontSize: "16px",
                fontSynthesis: "none",
                textRendering: "optimizelegibility",
                WebkitFontSmoothing: "antialiased",
                MozOsxFontSmoothing: "grayscale",
            },
            "html, body, #root": {
                color: "blackAlpha.800",
                height: "100%",
                margin: 0,
                padding: 0,
            },
        },
    },
    colors: {
        brandPrimary: {
            textBlue: "#001f33",
            darkBlue: "#002B49",
            lightBlue: "#5e8ab4",
            brightBlue: "#0085ca",
            darkGray: "#646464",
        },
        brandSecondary: {
            aqua: "#00677f",
            red: "#a6192e",
            orange: "#cf7f00",
            yellow: "#f9c20a",
            purple: "#470858",
            green: "#29702a",
        },
        blue: {
            25: "#f5f6f8",
            50: "#eff3f8",
            75: "#eaf1fa",
            100: "#cfdce9",
            150: "#c2d5e8",
            200: "#8eadcb",
            300: "#5e8ab4", // brand primary color light blue
            400: "#44769A",
            500: "#2C6086",
            600: "#194D72",
            700: "#0A3B5E",
            800: "#002d4d",
            900: "#002B49", // brand primary color dark blue
        },
        aqua: {
            50: "#cce1e5",
            100: "#80b3bf",
            200: "#4d95a5",
            300: "#338599",
            400: "#1a768c",
            500: "#00677f", // brand secondary color aqua
            600: "#005266",
            700: "#003e4c",
            800: "#002933",
            900: "#001f26",
        },
        cyan: {
            50: "#e6f3fa",
            100: "#b3daef",
            200: "#66b6df",
            300: "#339dd5",
            400: "#1a91cf",
            500: "#0085ca", // brand primary color bright blue
            600: "#0078b6",
            700: "#006aa2",
            800: "#005d8d",
            900: "#005079",
        },
        cherry: {
            50: "#edd1d5",
            100: "#d38c97",
            200: "#c15e6d",
            300: "#b84758",
            400: "#af3043",
            500: "#a6192e", // brand secondary color
            600: "#951729",
            700: "#851425",
            800: "#741220",
            900: "#640f1c",
        },
        orange: {
            50: "#f5e5cc",
            100: "#eccc99",
            200: "#e2b266",
            300: "#d99933",
            400: "#d48c1a",
            500: "#cf7f00", // brand secondary color
            600: "#ba7200",
            700: "#a66600",
            800: "#915900",
            900: "#7c4c00",
        },
        yellow: {
            50: "#fdedb6",
            100: "#fce185",
            200: "#fbda6c",
            300: "#fbd454",
            400: "#fac823",
            500: "#f9c20a", // brand secondary color
            600: "#e0af09",
            700: "#c79b08",
            800: "#ae8807",
            900: "#957406",
        },
        purple: {
            50: "#dacede",
            100: "#b59cbc",
            200: "#7e528a",
            300: "#6c3979",
            400: "#592169",
            500: "#470858", // brand secondary color
            600: "#40074f",
            700: "#390646",
            800: "#32063e",
            900: "#2b0535",
        },
        green: {
            50: "#d4e2d4",
            100: "#a9c6aa",
            200: "#7fa97f",
            300: "#548d55",
            400: "#3e7e3f",
            500: "#29702a", // brand secondary color
            600: "#256526",
            700: "#215a22",
            800: "#1d4e1d",
            900: "#194319",
        },
        gray: {
            50: "#f0f0f0",
            100: "#e0e0e0",
            200: "#d1d1d1",
            300: "#a2a2a2",
            400: "#838383",
            500: "#646464", // brand primary dark gray
            600: "#5a5a5a",
            700: "#505050",
            800: "#3c3c3c",
            900: "#282828",
        },
    },
    components: {
        Heading: defineStyleConfig({
            baseStyle: {
                color: "blackAlpha.800",
                fontWeight: 400,
            },
            variants: {
                page: defineStyle({
                    fontFamily: `'DIN Condensed', sans-serif`,
                    fontSize: "4xl",
                }),
                section: defineStyle({
                    fontSize: "xl",
                    mb: "1rem",
                }),
                info: defineStyle({
                    textTransform: "uppercase",
                    color: "gray.400",
                    fontSize: "xs",
                }),
                cardHeading: defineStyle({
                    color: "blackAlpha.800",
                    fontWeight: "bold",
                    fontSize: "lg",
                }),
            },
            defaultProps: {
                variant: "section",
            },
        }),
        Breadcrumb: defineBrdcmbMultiStyleConf({
            baseStyle: defineBrdcmbPartsStyle({
                link: {
                    color: "gray.500",
                },
            }),
        }),
        Text: defineStyleConfig({
            baseStyle: {
                color: "blackAlpha.800",
            },
            variants: {
                label: defineStyle({
                    color: "gray.400",
                    fontSize: "sm",
                    fontWeight: 700,
                }),
                info: defineStyle({
                    color: "gray.400",
                    fontSize: "sm",
                }),
            },
        }),
        Button: defineStyleConfig({
            variants: {
                primary: defineStyle({
                    backgroundColor: "blue.900",
                    color: "white",
                    _hover: {
                        bg: "blue.700",
                        _disabled: {
                            bg: "blue.900",
                        },
                    },
                    _active: { bg: "blue.900" },
                }),
                naked: defineStyle({
                    height: "unset",
                    minWidth: "unset",
                }),
            },
        }),
        Card: defineCardMultiStyleConfig({
            baseStyle: {
                container: {
                    borderRadius: "lg",
                    borderColor: "borderColor",
                    borderWidth: "1px",
                    boxShadow: 0,
                },
                header: {
                    borderTopRadius: "lg",
                    pb: 0,
                    "> *": {
                        mb: 0,
                    },
                },
                body: {},
                footer: {
                    pt: 0,
                },
            },
            variants: {
                panel: defineCardPartsStyle({
                    container: {
                        borderRadius: 0,
                        backgroundColor: "gray.50",
                        color: "blackAlpha.800",
                    },
                }),
                withShadow: defineCardPartsStyle({
                    container: {
                        boxShadow: ApplicationLayoutBoxShadow,
                    },
                }),
                naked: defineCardPartsStyle({
                    container: {
                        border: "none",
                    },
                }),
            },
        }),
        Modal: defineModalMultiStyleConfig({
            baseStyle: defineModalPartsStyle({
                body: {
                    p: "1rem",
                },
            }),
        }),
        Badge: defineStyleConfig({
            baseStyle: {
                borderRadius: "full",
                padding: ".1rem .5rem",
            },
            variants: {
                solid: defineStyle({}),
            },
            defaultProps: {
                variant: "solid",
                colorScheme: "gray",
            },
        }),
        Input: defineInputMultiStyleConfig({
            baseStyle: {
                addon: {},
                field: {
                    _placeholder: {
                        color: "gray.300",
                    },
                },
                element: {},
            },
            variants: {
                outline: defineInputPartsStyle({
                    element: {},
                }),
            },
            defaultProps: {
                variant: "outline",
            },
        }),
        FormLabel: defineStyleConfig({
            baseStyle: {
                color: "blackAlpha.800",
                fontWeight: 600,
            },
        }),
    },
};

export const theme = extendTheme(customTheme);
